import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BaseService } from '@/services/baseService';
import { queryKeys } from '@/lib/queryKeys';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

type MutationToastOptions = {
  showSuccessToast?: boolean;
};

export function useEntityQuery<T>(
  tableName: string,
  floorPlanId: string,
  service: BaseService<any> | { getByFloorPlan: (id: string) => Promise<T[]> },
) {
  return useQuery({
    queryKey: queryKeys.entitiesByFloorPlan(tableName, floorPlanId),
    queryFn: () => {
      if (!floorPlanId) return Promise.resolve([]);
      return service.getByFloorPlan(floorPlanId);
    },
    enabled: Boolean(floorPlanId),
    staleTime: 10 * 1000, // 10 seconds - shorter for better realtime sync
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

export function useEntityMutations<T>(
  tableName: string,
  floorPlanId: string,
  service:
    | BaseService<any>
    | {
        create: (item: any) => Promise<any>;
        update: (id: string, updates: any) => Promise<any>;
        delete: (id: string) => Promise<boolean>;
      },
) {
  const queryClient = useQueryClient();
  const queryKey = queryKeys.entitiesByFloorPlan(tableName, floorPlanId);

  // Create mutation with optimistic updates
  const createMutation = useMutation({
    mutationFn: ({ item }: { item: any; options?: MutationToastOptions }) => service.create(item),
    onMutate: async ({ item: newItem }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<T[]>(queryKey);

      // Create temporary item with temp ID
      const tempItem = {
        ...newItem,
        id: `temp-${Date.now()}`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Optimistically add to cache
      queryClient.setQueryData<T[]>(queryKey, (old = []) => [...old, tempItem as T]);

      logger.log(`[Optimistic] Created ${tableName} with temp ID:`, tempItem.id);

      // Return context object with snapshotted value
      return { previousData, tempItem };
    },
    onSuccess: (realItem, variables, context) => {
      // Replace the temporary item with the real one from server
      queryClient.setQueryData<T[]>(queryKey, (old = []) =>
        old.map(item => ((item as any).id === context?.tempItem.id ? realItem : item)),
      );

      logger.log(`[Optimistic] Replaced temp ${tableName} with real ID:`, realItem.id);
      if (variables.options?.showSuccessToast !== false) {
        toast.success('Created successfully');
      }
    },
    onError: (error: Error, variables, context) => {
      logger.error(`Failed to create ${tableName}:`, error);
      toast.error(`Failed to create: ${error.message}`);

      // Rollback to previous state on error
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      } else {
        // Force refetch to ensure cache is in sync
        queryClient.invalidateQueries({ queryKey });
      }
    },
    onSettled: (data, error) => {
      // Invalidate on error to ensure cache is in sync
      if (error) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });

  // Update mutation with optimistic updates
  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: any; showSuccessToast?: boolean }) => {
      // VF-62: never let an optimistic temp ID reach PostgREST — `id` is a
      // uuid column, so `temp-*` 400s. Happens when the user edits an entity
      // whose create hasn't resolved yet (e.g. assigning a load to a
      // just-created keypad while requests are stalled).
      if (id.startsWith('temp-')) {
        return Promise.reject(new Error('This item is still saving — try again in a moment'));
      }
      return service.update(id, updates);
    },
    onMutate: async ({ id, updates }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<T[]>(queryKey);

      // Optimistically update the item
      queryClient.setQueryData<T[]>(queryKey, (old = []) =>
        old.map(item => {
          if ((item as any).id === id) {
            return {
              ...item,
              ...updates,
              updated_at: new Date().toISOString(),
            } as T;
          }
          return item;
        }),
      );

      logger.log(`[Optimistic] Updated ${tableName}:`, id);

      // Return context object with snapshotted value
      return { previousData };
    },
    onSuccess: (_data, variables) => {
      if (variables.showSuccessToast !== false) {
        toast.success('Updated successfully');
      }
    },
    onError: (error: Error, variables, context) => {
      logger.error(`Failed to update ${tableName}:`, error);
      toast.error(`Failed to update: ${error.message}`);

      // Rollback to previous state on error
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
  });

  // Delete mutation with optimistic updates
  const deleteMutation = useMutation({
    mutationFn: ({ id }: { id: string; options?: MutationToastOptions }) => {
      // VF-62: same temp-ID guard as update — see above.
      if (id.startsWith('temp-')) {
        return Promise.reject(new Error('This item is still saving — try again in a moment'));
      }
      return service.delete(id);
    },
    onMutate: async ({ id }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<T[]>(queryKey);

      // Get the deleted item for potential rollback
      const deletedItem = previousData?.find((item: any) => item.id === id);

      // Optimistically remove the item
      queryClient.setQueryData<T[]>(queryKey, (old = []) =>
        old.filter(item => (item as any).id !== id),
      );

      logger.log(`[Optimistic] Deleted ${tableName}:`, id);

      // Return context object with snapshotted value
      return { previousData, deletedItem };
    },
    onSuccess: (_data, variables) => {
      if (variables.options?.showSuccessToast !== false) {
        toast.success('Deleted successfully');
      }
      // Invalidate to ensure cache is in sync with DB after delete
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error: Error, variables, context) => {
      logger.error(`Failed to delete ${tableName}:`, error);
      toast.error(`Failed to delete: ${error.message}`);

      // Rollback to previous state on error
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      } else {
        // Force refetch to ensure cache is in sync
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });

  return {
    create: (item: any, options?: MutationToastOptions) =>
      createMutation.mutateAsync({ item, options }),
    update: updateMutation.mutateAsync,
    delete: (id: string, options?: MutationToastOptions) =>
      deleteMutation.mutateAsync({ id, options }),
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}

// Combined hook for complete entity management
export function useEntity<T>(
  tableName: string,
  floorPlanId: string,
  service:
    | BaseService<any>
    | {
        getByFloorPlan: (id: string) => Promise<T[]>;
        create: (item: any) => Promise<any>;
        update: (id: string, updates: any) => Promise<any>;
        delete: (id: string) => Promise<boolean>;
      },
) {
  const query = useEntityQuery<T>(tableName, floorPlanId, service);
  const mutations = useEntityMutations<T>(tableName, floorPlanId, service);

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    ...mutations,
  };
}
